import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	get_page,
	get_page_documents,
	get_document_file,
	get_page_images,
	get_image_file,
	get_page_sections,
	get_user_info,
} from "../../api/functions";
import { useApi } from "../../api/api";
import { useAuth } from "../../provider/auth";
import { LoadingSpinner, DocumentViewer } from "../../components";
import { ErrorScreen } from "../";
import "./InfoPage.css";

const InfoPage = () => {
	const { pageId } = useParams();
	const { t, i18n } = useTranslation();
	const { isAuthenticated } = useAuth();
	const [page, setPage] = useState(null);
	const [images, setImages] = useState([]);
	const [imageUrls, setImageUrls] = useState({});
	const [sections, setSections] = useState([]);
	const [documents, setDocuments] = useState([]);
	const [selectedSection, setSelectedSection] = useState(null);
	const [viewingDoc, setViewingDoc] = useState(null);
	const [docUrl, setDocUrl] = useState(null);
	const [isPdf, setIsPdf] = useState(false);
	const [isWord, setIsWord] = useState(false);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const getPageApi = useApi(get_page);
	const getPageDocumentsApi = useApi(get_page_documents);
	const getDocumentFileApi = useApi(get_document_file);
	const getPageImagesApi = useApi(get_page_images);
	const getImageFileApi = useApi(get_image_file);
	const getPageSectionsApi = useApi(get_page_sections);
	const getUserInfoApi = useApi(get_user_info);

	useEffect(() => {
		const fetchAllPageData = async () => {
			if (!pageId) return;

			setIsLoading(true);
			setError(null);

			try {
				// Fetch user info if authenticated to check membership
				let userInfo = null;
				if (isAuthenticated) {
					try {
						userInfo = await getUserInfoApi.execute();
					} catch (err) {
						console.error("Error fetching user info:", err);
					}
				}

				// Fetch page data to check permissions
				const pageData = await getPageApi.execute({
					page_id: parseInt(pageId),
				});

				if (!pageData) {
					setError(404);
					setIsLoading(false);
					return;
				}

				// Check if page is private and user is not a member
				if (pageData.private && (!isAuthenticated || !userInfo?.is_member)) {
					setError(403);
					setIsLoading(false);
					return;
				}

				setPage(pageData);

				// Fetch all related data in parallel
				const [imagesData, sectionsData, documentsData] = await Promise.all([
					getPageImagesApi
						.execute({ page_id: parseInt(pageId) })
						.catch(() => []),
					getPageSectionsApi
						.execute({ page_id: parseInt(pageId) })
						.catch(() => []),
					getPageDocumentsApi
						.execute({ page_id: parseInt(pageId) })
						.catch(() => []),
				]);

				setImages(imagesData || []);

				const urlMap = {};
				await Promise.all(
					(imagesData || []).map(async (img) => {
						try {
							const blob = await getImageFileApi.execute({ image_id: img.id });
							if (blob) {
								urlMap[img.id] = URL.createObjectURL(blob);
							}
						} catch (err) {
							console.error(`Failed to load image ${img.id}:`, err);
						}
					}),
				);
				setImageUrls(urlMap);

				setSections(sectionsData || []);
				setDocuments(documentsData || []);

				// Set first section as selected by default if sections exist
				if (sectionsData && sectionsData.length > 0) {
					setSelectedSection(sectionsData[0]);
				}
			} catch (err) {
				console.error("Error fetching page data:", err);
				setError(404);
			} finally {
				setIsLoading(false);
			}
		};

		fetchAllPageData();
	}, [pageId, isAuthenticated]);

	useEffect(() => {
		return () => {
			if (docUrl) {
				URL.revokeObjectURL(docUrl);
			}
		};
	}, [docUrl]);

	useEffect(() => {
		return () => {
			Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
		};
	}, [imageUrls]);

	const getLocalizedField = (item, fieldPrefix) => {
		if (!item) return "";
		const language = i18n.language === "cs" ? "cz" : i18n.language;
		if (item[`${fieldPrefix}_${language}`]) {
			return item[`${fieldPrefix}_${language}`];
		}
		if (item[`${fieldPrefix}_cz`]) {
			return item[`${fieldPrefix}_cz`];
		}
		if (item[`${fieldPrefix}_en`]) {
			return item[`${fieldPrefix}_en`];
		}
		if (item[`${fieldPrefix}_de`]) {
			return item[`${fieldPrefix}_de`];
		}
		return "";
	};

	const handleViewDocument = async (doc) => {
		try {
			const blob = await getDocumentFileApi.execute({ document_id: doc.id });
			if (blob) {
				const url = URL.createObjectURL(blob);
				setDocUrl(url);

				const isPdfDoc =
					blob.type === "application/pdf" ||
					doc.document_filename?.toLowerCase().endsWith(".pdf");
				const isWordDoc =
					blob.type === "application/msword" ||
					blob.type ===
						"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
					doc.document_filename?.toLowerCase().endsWith(".doc") ||
					doc.document_filename?.toLowerCase().endsWith(".docx");

				setIsPdf(isPdfDoc);
				setIsWord(isWordDoc);
				setViewingDoc(doc);
			}
		} catch (error) {
			console.error("Failed to load document:", error);
		}
	};

	const handleDownloadDocument = async (doc) => {
		try {
			const blob = await getDocumentFileApi.execute({ document_id: doc.id });
			if (blob) {
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = doc.document_filename || "document";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
		} catch (error) {
			console.error("Failed to download document:", error);
		}
	};

	const handleCloseViewer = () => {
		if (docUrl) {
			URL.revokeObjectURL(docUrl);
		}
		setDocUrl(null);
		setViewingDoc(null);
		setIsPdf(false);
		setIsWord(false);
	};

	if (error) {
		return <ErrorScreen error={error} />;
	}

	if (isLoading || !page) {
		return (
			<div className="info-page">
				<main className="info-page-main">
					<div className="info-page-container">
						<LoadingSpinner message="Loading page..." />
					</div>
				</main>
			</div>
		);
	}

	const title = getLocalizedField(page, "title") || "Untitled Page";
	const pageText = getLocalizedField(page, "text") || "";
	const sectionText = selectedSection
		? getLocalizedField(selectedSection, "text")
		: "";
	const displayText = sections.length > 0 ? sectionText : pageText;

	return (
		<div className="info-page">
			<main className="info-page-main">
				<div className="info-page-container">
					{/* Page Title */}
					<h1 className="info-page-title">{title}</h1>

					{/* Images Section */}
					{images.length > 0 && (
						<div className="info-page-images">
							{images.map((image) => (
								<div key={image.id} className="info-page-image-wrapper">
									<img
										src={imageUrls[image.id]}
										alt={getLocalizedField(image, "title") || "Page image"}
										className="info-page-image"
										loading="lazy"
									/>
								</div>
							))}
						</div>
					)}

					{/* Section Buttons */}
					{sections.length > 1 && (
						<div className="info-page-sections-nav">
							{sections.map((section) => (
								<button
									key={section.id}
									className={`info-page-section-btn ${
										selectedSection?.id === section.id ? "active" : ""
									}`}
									onClick={() => setSelectedSection(section)}
								>
									{getLocalizedField(section, "title") || "Untitled Section"}
								</button>
							))}
						</div>
					)}

					{/* Content Section */}
					{displayText && (
						<div className="info-page-content">
							<ReactMarkdown remarkPlugins={[remarkGfm]}>
								{displayText}
							</ReactMarkdown>
						</div>
					)}

					{/* Documents Section */}
					{documents.length > 0 && (
						<div className="info-page-documents">
							<h2 className="info-page-documents-title">
								{t("infoPage.documentsTitle", "Related Documents")}
							</h2>
							<div className="info-page-documents-grid">
								{documents.map((doc) => (
									<div key={doc.id} className="info-page-document-card">
										<svg
											width="48"
											height="48"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M9 12h6M9 16h6"
											/>
										</svg>
										<span className="info-page-document-title">
											{getLocalizedField(doc, "title") ||
												doc.document_filename ||
												"Untitled Document"}
										</span>
										<div className="info-page-document-actions">
											<button
												className="info-page-document-btn view-btn"
												onClick={() => handleViewDocument(doc)}
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
												>
													<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
													<circle cx="12" cy="12" r="3" />
												</svg>
											</button>
											<button
												className="info-page-document-btn download-btn"
												onClick={() => handleDownloadDocument(doc)}
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
													/>
												</svg>
											</button>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Document Viewer */}
					{viewingDoc && docUrl && (
						<DocumentViewer
							docUrl={docUrl}
							isPdf={isPdf}
							isWord={isWord}
							documentFilename={viewingDoc.document_filename}
							initialFullscreen={true}
							onClose={handleCloseViewer}
						/>
					)}
				</div>
			</main>
		</div>
	);
};

export default InfoPage;
